import type { TableName } from '../TableSidebar';

export interface ParentFK {
  column: string;
  refTable: TableName;
  refColumn: string;
  labelColumns: string[];
  isArray?: boolean;
  isText?: boolean;
}

export interface ChildRel {
  table: TableName;
  via: { localCol: string; childCol: string }[];
  label: string;
}

export interface TableRelations {
  parents: ParentFK[];
  children: ChildRel[];
}

const empty: TableRelations = { parents: [], children: [] };

export const RELATIONSHIPS: Record<TableName, TableRelations> = {
  // ----- Lookup roots -----
  world: {
    parents: [],
    children: [
      { table: 'stage', via: [{ localCol: 'id', childCol: 'world_id' }], label: 'stages' },
    ],
  },
  map: {
    parents: [],
    children: [
      { table: 'stage', via: [{ localCol: 'id', childCol: 'map_id' }], label: 'stages' },
      {
        table: 'league_table',
        via: [{ localCol: 'id', childCol: 'map_id' }],
        label: 'league tables',
      },
      { table: 'valhalla', via: [{ localCol: 'id', childCol: 'map_id' }], label: 'valhallas' },
    ],
  },
  enemy: {
    parents: [],
    children: [
      {
        table: 'stage_wave_enemy',
        via: [{ localCol: 'id', childCol: 'enemy_id' }],
        label: 'stage spawns',
      },
      {
        table: 'league_table_wave_enemy',
        via: [{ localCol: 'id', childCol: 'enemy_id' }],
        label: 'league spawns',
      },
      {
        table: 'valhalla_wave_enemy',
        via: [{ localCol: 'id', childCol: 'enemy_id' }],
        label: 'valhalla spawns',
      },
      {
        table: 'degen_league_wave_enemy',
        via: [{ localCol: 'id', childCol: 'enemy_id' }],
        label: 'degen spawns',
      },
    ],
  },
  meta_tag: {
    parents: [],
    children: [
      {
        table: 'meta_tag_synergy',
        via: [{ localCol: 'id', childCol: 'meta_tag_id' }],
        label: 'synergies',
      },
    ],
  },
  pet_grade: {
    parents: [],
    children: [{ table: 'pet', via: [{ localCol: 'grade', childCol: 'grade' }], label: 'pets' }],
  },
  wave_shop_config: {
    parents: [],
    children: [
      {
        table: 'item_group',
        via: [{ localCol: 'id', childCol: 'wave_shop_config_id' }],
        label: 'item groups',
      },
      {
        table: 'stage_wave',
        via: [{ localCol: 'id', childCol: 'shop_config_id' }],
        label: 'stage waves',
      },
      {
        table: 'league_table_wave',
        via: [{ localCol: 'id', childCol: 'shop_config_id' }],
        label: 'league waves',
      },
      {
        table: 'valhalla_wave',
        via: [{ localCol: 'id', childCol: 'shop_config_id' }],
        label: 'valhalla waves',
      },
      {
        table: 'degen_league_wave',
        via: [{ localCol: 'id', childCol: 'shop_config_id' }],
        label: 'degen waves',
      },
    ],
  },
  item: {
    parents: [
      {
        column: 'meta_tag_ids',
        refTable: 'meta_tag',
        refColumn: 'id',
        labelColumns: ['name'],
        isArray: true,
      },
      {
        column: 'summon_pet_ids',
        refTable: 'pet',
        refColumn: 'id',
        labelColumns: ['name'],
        isArray: true,
      },
    ],
    children: [
      {
        table: 'item_group',
        via: [{ localCol: 'id', childCol: 'item_id' }],
        label: 'shop entries',
      },
    ],
  },
  character: {
    parents: [],
    children: [
      { table: 'skin', via: [{ localCol: 'id', childCol: 'character_id' }], label: 'skins' },
    ],
  },
  league_table: {
    parents: [
      { column: 'map_id', refTable: 'map', refColumn: 'id', labelColumns: ['id'], isText: true },
      {
        column: 'meta_tags',
        refTable: 'meta_tag',
        refColumn: 'id',
        labelColumns: ['name'],
        isArray: true,
      },
    ],
    children: [
      {
        table: 'league_table_wave',
        via: [{ localCol: 'id', childCol: 'league_table_id' }],
        label: 'waves',
      },
      {
        table: 'league_season',
        via: [{ localCol: 'id', childCol: 'league_table_id' }],
        label: 'seasons using this table',
      },
    ],
  },
  league_season: {
    parents: [
      {
        column: 'league_table_id',
        refTable: 'league_table',
        refColumn: 'id',
        labelColumns: ['name'],
      },
      {
        column: 'reward_table_id',
        refTable: 'league_reward',
        refColumn: 'reward_table_id',
        labelColumns: [],
      },
      {
        column: 'reward_table_id_web2',
        refTable: 'league_reward',
        refColumn: 'reward_table_id',
        labelColumns: [],
      },
      {
        column: 'reward_table_id_web3',
        refTable: 'league_reward',
        refColumn: 'reward_table_id',
        labelColumns: [],
      },
    ],
    children: [],
  },
  valhalla: {
    parents: [
      { column: 'map_id', refTable: 'map', refColumn: 'id', labelColumns: ['id'], isText: true },
      {
        column: 'meta_tags',
        refTable: 'meta_tag',
        refColumn: 'id',
        labelColumns: ['name'],
        isArray: true,
      },
    ],
    children: [
      {
        table: 'valhalla_wave',
        via: [{ localCol: 'id', childCol: 'valhalla_id' }],
        label: 'waves',
      },
      {
        table: 'valhalla_season',
        via: [{ localCol: 'id', childCol: 'valhalla_table_id' }],
        label: 'seasons using this table',
      },
    ],
  },
  valhalla_season: {
    parents: [
      {
        column: 'valhalla_table_id',
        refTable: 'valhalla',
        refColumn: 'id',
        labelColumns: ['name'],
      },
    ],
    children: [],
  },
  degen_league: {
    parents: [
      {
        column: 'meta_tags',
        refTable: 'meta_tag',
        refColumn: 'id',
        labelColumns: ['name'],
        isArray: true,
      },
    ],
    children: [
      {
        table: 'degen_league_wave',
        via: [{ localCol: 'id', childCol: 'degen_league_id' }],
        label: 'waves',
      },
    ],
  },
  pet: {
    parents: [
      { column: 'grade', refTable: 'pet_grade', refColumn: 'grade', labelColumns: ['name'] },
      {
        column: 'meta_tag_ids',
        refTable: 'meta_tag',
        refColumn: 'id',
        labelColumns: ['name'],
        isArray: true,
      },
    ],
    children: [
      { table: 'pet_upgrade', via: [{ localCol: 'id', childCol: 'pet_id' }], label: 'upgrades' },
      {
        table: 'pet_summon_probability',
        via: [{ localCol: 'id', childCol: 'pet_id' }],
        label: 'summon weights',
      },
    ],
  },
  stage: {
    parents: [
      { column: 'world_id', refTable: 'world', refColumn: 'id', labelColumns: ['name'] },
      { column: 'map_id', refTable: 'map', refColumn: 'id', labelColumns: ['id'], isText: true },
      {
        column: 'meta_tags',
        refTable: 'meta_tag',
        refColumn: 'id',
        labelColumns: ['name'],
        isArray: true,
      },
    ],
    children: [
      { table: 'stage_wave', via: [{ localCol: 'id', childCol: 'stage_id' }], label: 'waves' },
    ],
  },
  stage_wave: {
    parents: [
      { column: 'stage_id', refTable: 'stage', refColumn: 'id', labelColumns: ['name'] },
      {
        column: 'shop_config_id',
        refTable: 'wave_shop_config',
        refColumn: 'id',
        labelColumns: ['id'],
      },
    ],
    children: [
      {
        table: 'stage_wave_enemy',
        via: [
          { localCol: 'stage_id', childCol: 'stage_id' },
          { localCol: 'wave_number', childCol: 'wave_number' },
        ],
        label: 'enemies',
      },
    ],
  },
  stage_wave_enemy: {
    parents: [{ column: 'enemy_id', refTable: 'enemy', refColumn: 'id', labelColumns: ['name'] }],
    children: [],
  },
  league_table_wave: {
    parents: [
      {
        column: 'league_table_id',
        refTable: 'league_table',
        refColumn: 'id',
        labelColumns: ['name'],
      },
      {
        column: 'shop_config_id',
        refTable: 'wave_shop_config',
        refColumn: 'id',
        labelColumns: ['id'],
      },
    ],
    children: [
      {
        table: 'league_table_wave_enemy',
        via: [
          { localCol: 'league_table_id', childCol: 'league_table_id' },
          { localCol: 'wave_number', childCol: 'wave_number' },
        ],
        label: 'enemies',
      },
    ],
  },
  league_table_wave_enemy: {
    parents: [{ column: 'enemy_id', refTable: 'enemy', refColumn: 'id', labelColumns: ['name'] }],
    children: [],
  },
  valhalla_wave: {
    parents: [
      { column: 'valhalla_id', refTable: 'valhalla', refColumn: 'id', labelColumns: ['name'] },
      {
        column: 'shop_config_id',
        refTable: 'wave_shop_config',
        refColumn: 'id',
        labelColumns: ['id'],
      },
    ],
    children: [
      {
        table: 'valhalla_wave_enemy',
        via: [
          { localCol: 'valhalla_id', childCol: 'valhalla_id' },
          { localCol: 'wave_number', childCol: 'wave_number' },
        ],
        label: 'enemies',
      },
    ],
  },
  valhalla_wave_enemy: {
    parents: [{ column: 'enemy_id', refTable: 'enemy', refColumn: 'id', labelColumns: ['name'] }],
    children: [],
  },
  degen_league_wave: {
    parents: [
      {
        column: 'degen_league_id',
        refTable: 'degen_league',
        refColumn: 'id',
        labelColumns: ['name'],
      },
      {
        column: 'shop_config_id',
        refTable: 'wave_shop_config',
        refColumn: 'id',
        labelColumns: ['id'],
      },
    ],
    children: [
      {
        table: 'degen_league_wave_enemy',
        via: [
          { localCol: 'degen_league_id', childCol: 'degen_league_id' },
          { localCol: 'wave_number', childCol: 'wave_number' },
        ],
        label: 'enemies',
      },
    ],
  },
  degen_league_wave_enemy: {
    parents: [{ column: 'enemy_id', refTable: 'enemy', refColumn: 'id', labelColumns: ['name'] }],
    children: [],
  },
  skin: {
    parents: [
      { column: 'character_id', refTable: 'character', refColumn: 'id', labelColumns: ['id'] },
    ],
    children: [],
  },
  pet_upgrade: {
    parents: [{ column: 'pet_id', refTable: 'pet', refColumn: 'id', labelColumns: ['name'] }],
    children: [],
  },
  pet_summon_probability: {
    parents: [
      {
        column: 'level',
        refTable: 'pet_summon_level',
        refColumn: 'level',
        labelColumns: ['level'],
      },
      { column: 'pet_id', refTable: 'pet', refColumn: 'id', labelColumns: ['name'] },
    ],
    children: [],
  },
  meta_tag_synergy: {
    parents: [
      { column: 'meta_tag_id', refTable: 'meta_tag', refColumn: 'id', labelColumns: ['name'] },
    ],
    children: [],
  },
  item_group: {
    parents: [
      {
        column: 'wave_shop_config_id',
        refTable: 'wave_shop_config',
        refColumn: 'id',
        labelColumns: ['id'],
      },
      { column: 'item_id', refTable: 'item', refColumn: 'id', labelColumns: ['name'] },
    ],
    children: [],
  },

  // ----- Tables with no FK relationships -----
  game_config: empty,
  game_difficulty: empty,
  pet_summon_config: empty,
  pet_summon_level: {
    parents: [],
    children: [
      {
        table: 'pet_summon_probability',
        via: [{ localCol: 'level', childCol: 'level' }],
        label: 'probabilities',
      },
    ],
  },
  consumable_item: empty,
  upgrade_config: empty,
  skill: empty,
  quest: empty,
  quest_milestone: empty,
  guide_quest: empty,
  attendance_reward: empty,
  event_attendance: empty,
  league_reward: empty,
  product: empty,
  entitlement: empty,
  payment_event: empty,
  // 글로리 샵 상품 — delivery_items/cost 는 RewardKey·재화 키를 담은 jsonb 라 DB FK 가 없다.
  glory_shop_item: empty,
  free_shop_item: empty,
  // 글로리 뽑기 확률표 엔트리 — box_id 는 glory_shop_item.gacha_box_id 와 매칭되지만 DB FK 는
  // 없다(마이그레이션 주석 참고: 상품 행 교체·삭제가 확률표 때문에 막히는 걸 피하려는 의도).
  glory_gacha_entry: empty,
  degen_rune_pool: empty,
  degen_conditional_pool: empty,
  degen_conditional_count_prob: empty,
  degen_season: {
    // 난이도별 몬스터 테이블 FK — degen_league(id) 를 이름 드롭다운으로 편집.
    parents: [
      {
        column: 'norns_table_id',
        refTable: 'degen_league',
        refColumn: 'id',
        labelColumns: ['name'],
      },
      {
        column: 'jotnar_table_id',
        refTable: 'degen_league',
        refColumn: 'id',
        labelColumns: ['name'],
      },
      {
        column: 'ragnarok_table_id',
        refTable: 'degen_league',
        refColumn: 'id',
        labelColumns: ['name'],
      },
      {
        column: 'reward_table_id',
        refTable: 'degen_season_reward',
        refColumn: 'reward_table_id',
        labelColumns: [],
      },
    ],
    children: [],
  },
  // ----- 폴크방 총력전 -----
  // boss 가 공격 타임라인(boss_pattern)과 시즌의 부모다 — 편성표가 폐지되면서
  // 시즌 행이 boss_id 를 직접 든다. season 의 rune_pool_id 는 그룹 식별자일 뿐
  // 부모 테이블이 없다(league_reward.reward_table_id 와 같은 패턴).
  folkvangr_boss: {
    parents: [],
    children: [
      {
        table: 'folkvangr_season',
        via: [{ localCol: 'id', childCol: 'boss_id' }],
        label: 'seasons',
      },
      {
        table: 'folkvangr_boss_pattern',
        via: [{ localCol: 'id', childCol: 'boss_id' }],
        label: 'patterns',
      },
    ],
  },
  folkvangr_season: {
    parents: [
      {
        column: 'boss_id',
        refTable: 'folkvangr_boss',
        refColumn: 'id',
        labelColumns: ['name_key'],
      },
      {
        column: 'omen_id',
        refTable: 'folkvangr_omen',
        refColumn: 'id',
        labelColumns: ['name_key'],
      },
    ],
    children: [],
  },
  folkvangr_attack_pattern: {
    parents: [],
    children: [
      {
        table: 'folkvangr_boss_pattern',
        via: [{ localCol: 'id', childCol: 'pattern_id' }],
        label: 'used by',
      },
    ],
  },
  folkvangr_boss_pattern: {
    parents: [
      {
        column: 'boss_id',
        refTable: 'folkvangr_boss',
        refColumn: 'id',
        labelColumns: ['name_key'],
      },
      {
        column: 'pattern_id',
        refTable: 'folkvangr_attack_pattern',
        refColumn: 'id',
        labelColumns: ['name'],
      },
    ],
    children: [],
  },
  folkvangr_grade_stat: empty,
  folkvangr_omen: empty,
  folkvangr_reward: empty,
  degen_season_reward: empty,
};
